import {
  userSettings,
  setUserStateSettings,
  saveUserSettings,
} from "@/globals/userSettings";
import { ILanguage, LANGUAGES } from "../../i18n/Languages";
import { changeLanguage } from "@/i18n/changeLanguage";
import arrowIcon from "@/icons/arrowIcon.svg";
import { pluginConfig } from "@/globals/pluginConfig";
import { getDefaultLanguage } from "@/i18n/getDefaultLanguage";


  // *** Translations ***
  if (!LANGUAGES.some((lang) => lang.code === userSettings.lang)) {
    userSettings.lang = getDefaultLanguage();
  }


  // Helper to manage outside click
  let dropdownOpen = false;
  let outsideClickHandler: any = null;

  // Short code shown in the round badge. Use only the primary language subtag
  // so long BCP-47 codes (e.g. "zh-Hans") still fit the fixed-size circle.
  const langBadge = (code?: string) => (code || "").split("-")[0].toUpperCase();



export   function renderLanguageDropdown($menu: HTMLElement, filter = "") {
  // Filter languages based on search
  const $langDropdown = $menu.querySelector("#cya11y-language-dropdown") as HTMLElement;

  const filteredLangs = LANGUAGES.filter(lang =>
    lang.label.toLowerCase().includes(filter.toLowerCase()) ||
    lang.code.toLowerCase().includes(filter.toLowerCase())
  );
  const selectedIndex = filteredLangs.findIndex(l => l.code === userSettings.lang);

  // Build dropdown HTML with ARIA roles/attributes
  $langDropdown.innerHTML = `
    <div class="cya11y-custom-select" role="combobox" aria-haspopup="listbox" aria-expanded="${dropdownOpen}">
      <div class="cya11y-selected-lang" id="cya11y-selected-lang" aria-label="Selected language" aria-controls="cya11y-lang-list" aria-haspopup="listbox" tabindex="0">
        <span class="cya11y-lang-code">${langBadge(userSettings.lang)}</span>
        <span class="cya11y-lang-label">${LANGUAGES.find(l => l.code === userSettings.lang)?.label}</span>
        <span class="cya11y-arrow" aria-hidden="true">
          ${arrowIcon}
        </span>
      </div>
      <div class="cya11y-dropdown-list" id="cya11y-lang-list" role="listbox" style="display:${dropdownOpen ? 'block' : 'none'};">
        <input type="text" id="cya11y-lang-search" class="cya11y-lang-search" placeholder="Search languages" value="${filter}" aria-label="Search languages" />
        <div class="cya11y-lang-options">
          ${filteredLangs.map((lang, i) => `
            <div class="cya11y-lang-option${lang.code === userSettings.lang ? " selected" : ""}" role="option" aria-selected="${lang.code === userSettings.lang}" data-code="${lang.code}" tabindex="-1" data-index="${i}">
              <span class="cya11y-lang-code">${langBadge(lang.code)}</span>
              <span class="cya11y-lang-label">${lang.label}</span>
            </div>
          `).join("")}
        </div>
      </div>
    </div>
  `;

  // Add event listeners
  const $selected = $langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement;
  const $dropdown = $langDropdown.querySelector(".cya11y-dropdown-list") as HTMLElement;
  const $search = $langDropdown.querySelector(".cya11y-lang-search") as HTMLInputElement;
  const $optionsContainer = $dropdown.querySelector(".cya11y-lang-options") as HTMLElement;
  let focusedOption = selectedIndex >= 0 ? selectedIndex : 0;

  // Open/close on click
  $selected.onclick = (e) => {
    e.stopPropagation();
    if (dropdownOpen) {
      closeDropdown($langDropdown);
      $selected.querySelector('.cya11y-arrow')?.classList.remove('cya11y-arrow-rotate');
    } else {
      openDropdown($langDropdown);
      $selected.querySelector('.cya11y-arrow')?.classList.add('cya11y-arrow-rotate');
      $search?.focus();
    }
  };
  $selected.onkeydown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      openDropdown($langDropdown);
      $selected.querySelector('.cya11y-arrow')?.classList.add('cya11y-arrow-rotate');
      setTimeout(() => $search?.focus(), 0);
    }
  };

  // Search input
  $search?.addEventListener("input", (e) => {
    const searchValue = $search.value;
    const $dropdown = $langDropdown.querySelector(".cya11y-dropdown-list") as HTMLElement;
    const $optionsContainer = $dropdown.querySelector(".cya11y-lang-options") as HTMLElement;
    
    // Filter languages based on search
    const filteredLangs = LANGUAGES.filter(lang =>
      lang.label.toLowerCase().includes(searchValue.toLowerCase()) ||
      lang.code.toLowerCase().includes(searchValue.toLowerCase())
    );

    // Update only the options list without re-rendering the entire dropdown
    $optionsContainer.innerHTML = filteredLangs.map((lang, i) => `
      <div class="cya11y-lang-option${lang.code === userSettings.lang ? " selected" : ""}" role="option" aria-selected="${lang.code === userSettings.lang}" data-code="${lang.code}" tabindex="-1" data-index="${i}">
        <span class="cya11y-lang-code">${langBadge(lang.code)}</span>
        <span class="cya11y-lang-label">${lang.label}</span>
      </div>
    `).join("");

    // Reattach event listeners to the new options
    const $newOptions = Array.from($optionsContainer.querySelectorAll(".cya11y-lang-option")) as HTMLElement[];
    $newOptions.forEach(($option, i) => {
      $option.onclick = (e) => {
        e.stopPropagation();
        changeLanguage($option.dataset.code, $menu);
        closeDropdown($langDropdown);
        renderLanguageDropdown($menu);
        ($langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement)?.focus();
      };
    });
  });

  // Handle keyboard navigation for search input
  $search?.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const $filteredOptions = Array.from($langDropdown.querySelectorAll(".cya11y-lang-option")) as HTMLElement[];
      $filteredOptions[0].focus();
      $filteredOptions.forEach((option, index) => {
        option.addEventListener("keydown", (e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (index < $filteredOptions.length - 1) {
              $filteredOptions[index + 1].focus();
            }
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (index > 0) {
              $filteredOptions[index - 1].focus();
            } else {
              $search.focus();  
            }
          } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            changeLanguage(option.dataset.code, $menu);
            closeDropdown($langDropdown);
            renderLanguageDropdown($menu);
            ($langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement)?.focus();
          } else if (e.key === "Escape") {
            closeDropdown($langDropdown);
            $selected.focus();
          }
        });
      });
    }
    
  });

  // Add focus management and keyboard navigation for options
  const $allOptions = Array.from($langDropdown.querySelectorAll(".cya11y-lang-option")) as HTMLElement[];
  $allOptions.forEach(($option, index) => {
    // Focus management
    $option.addEventListener("focus", () => {
      $option.setAttribute("aria-selected", "true");
      $allOptions.forEach(opt => {
        if (opt !== $option) {
          opt.setAttribute("aria-selected", "false");
        }
      });
    });

    // Click handler
    $option.addEventListener("click", () => {
      changeLanguage($option.dataset.code, $menu);
      closeDropdown($langDropdown);
      renderLanguageDropdown($menu);
      ($langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement)?.focus();
    });

    // Keyboard navigation
    $option.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (index < $allOptions.length - 1) {
          $allOptions[index + 1].focus();
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (index > 0) {
          $allOptions[index - 1].focus();
        } else {
          $search.focus();
        }
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        changeLanguage($option.dataset.code, $menu);
        closeDropdown($langDropdown);
        renderLanguageDropdown($menu);
        ($langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement)?.focus();
      } else if (e.key === "Escape") {
        closeDropdown($langDropdown);
        ($langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement)?.focus();
      }
    });
  });

  // Ensure dropdown is open when search input is focused
  $search?.addEventListener("focus", () => {
    openDropdown($langDropdown);
  });

  // Close dropdown when focus leaves it entirely (Tab key or click-outside via keyboard).
  // Deferred to next tick so that a mousedown on a non-focusable child of an option
  // (the inner <span>s) doesn't blur the search input and close the dropdown before
  // the option's click handler can fire.
  // The widget runs inside a Shadow DOM, so `document.activeElement` would resolve
  // to the shadow host — we need the root node's activeElement instead.
  $langDropdown.addEventListener("focusout", () => {
    setTimeout(() => {
      const root = $langDropdown.getRootNode() as Document | ShadowRoot;
      const active = (root as Document | ShadowRoot).activeElement;
      if (!active || !$langDropdown.contains(active)) {
        closeDropdown($langDropdown);
      }
    }, 0);
  });
}

function closeDropdown($langDropdown: HTMLElement) {
  const $dropdown = $langDropdown.querySelector(".cya11y-dropdown-list") as HTMLElement;
  const $selected = $langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement;
  if ($dropdown) $dropdown.style.display = "none";
  $selected.querySelector('.cya11y-arrow')?.classList.remove('cya11y-arrow-rotate');
  dropdownOpen = false;
  if (outsideClickHandler) {
    document.removeEventListener("mousedown", outsideClickHandler);
    outsideClickHandler = null;
  }
}

function openDropdown($langDropdown: HTMLElement) {
  const $dropdown = $langDropdown.querySelector(".cya11y-dropdown-list") as HTMLElement;
  const $selected = $langDropdown.querySelector(".cya11y-selected-lang") as HTMLElement;
  if ($dropdown) $dropdown.style.display = "block";
  $selected.querySelector('.cya11y-arrow')?.classList.add('cya11y-arrow-rotate');
  dropdownOpen = true;
  // Attach outside click handler. The widget lives in a Shadow DOM, so at the
  // document level `e.target` is retargeted to the shadow host — which is
  // always outside `$langDropdown`. Use composedPath() to see the real
  // event path (including inside-shadow elements) and only close when the
  // mousedown truly happened outside the dropdown.
  if (!outsideClickHandler) {
    outsideClickHandler = (e: MouseEvent) => {
      const path = (typeof e.composedPath === "function" ? e.composedPath() : []) as EventTarget[];
      if (!path.includes($langDropdown)) {
        closeDropdown($langDropdown);
      }
    };
    document.addEventListener("mousedown", outsideClickHandler);
  }
}